import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Download, Send, User } from "lucide-react";
import { STAGE_META, STAGE_ORDER, PRIORITY_META, money, fmtDateTime, acv } from "./crmMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  "lead_created", "lead_assigned", "stage_changed", "note", "value_updated", "priority_changed", "followup_set",
  "site_visit_scheduled", "site_visit_completed", "proposal_generated", "proposal_sent", "proposal_viewed",
  "proposal_signed", "job_scheduled", "job_started", "job_completed", "invoice_sent", "payment_received", "recurring_service_created",
];

export default function LeadDrawer({ lead, team, token, onClose, onSaved }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [form, setForm] = useState(null);
  const [activity, setActivity] = useState([]);
  const [filter, setFilter] = useState("");
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("details");

  const loadActivity = useCallback(async (type) => {
    if (!lead) return;
    const q = type ? `?type=${type}` : "";
    try { const r = await axios.get(`${API}/admin/leads/${lead.id}/activity${q}`, auth); setActivity(r.data); } catch { /* ignore */ }
  }, [lead]); // eslint-disable-line

  useEffect(() => {
    if (lead) {
      setForm({
        estimated_monthly_value: lead.estimated_monthly_value || 0,
        assigned_to: lead.assigned_to || "",
        priority: lead.priority || "medium",
        next_action: lead.next_action || "",
        follow_up_date: lead.follow_up_date || "",
      });
      setTab("details");
      setFilter("");
      loadActivity("");
    }
  }, [lead, loadActivity]);

  if (!lead) return null;
  const m = STAGE_META[lead.stage] || STAGE_META.new_lead;

  const save = async () => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, {
        estimated_monthly_value: Number(form.estimated_monthly_value),
        assigned_to: form.assigned_to,
        priority: form.priority,
        next_action: form.next_action,
        follow_up_date: form.follow_up_date || null,
      }, auth);
      toast.success("Lead updated");
      onSaved();
      loadActivity(filter);
    } catch { toast.error("Couldn't save changes."); }
  };

  const changeStage = async (stage) => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}/stage?stage=${stage}`, {}, auth);
      toast.success(`Stage: ${STAGE_META[stage].label}`);
      onSaved();
      loadActivity(filter);
    } catch { toast.error("Couldn't change stage."); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await axios.post(`${API}/admin/leads/${lead.id}/notes`, { text: note }, auth); setNote(""); toast.success("Note added"); loadActivity(filter); }
    catch { toast.error("Couldn't add note."); }
  };

  const exportCsv = () => {
    const rows = [["Date", "Type", "Actor", "Message"], ...activity.map((a) => [a.at, a.type, a.actor, (a.message || "").replace(/"/g, "'")])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${lead.lead_id}-activity.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[70] bg-ink-950/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="lead-drawer-overlay">
        <motion.div data-testid="lead-drawer" onClick={(e) => e.stopPropagation()}
          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="absolute right-0 top-0 h-full w-full max-w-lg bg-ink-900 border-l border-white/10 flex flex-col">
          <div className="p-6 border-b border-white/8 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-600" style={{ background: `${m.color}22`, color: m.color }}><m.icon className="h-3 w-3" /> {m.label}</span>
                <span className="text-steel-light text-xs font-600">{lead.lead_id}</span>
              </div>
              <h2 className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{lead.company_name}</h2>
              <p className="text-white/45 text-sm">{lead.contact_person} · {lead.email}</p>
            </div>
            <button data-testid="lead-drawer-close" onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex gap-1 px-6 pt-4">
            {[["details", "Details"], ["timeline", "Activity Timeline"]].map(([k, l]) => (
              <button key={k} data-testid={`drawer-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-2 rounded-full text-sm font-600 transition-colors ${tab === k ? "bg-white text-ink-950" : "text-white/55 hover:text-white"}`}>{l}</button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {tab === "details" ? (
              <div className="space-y-5" data-testid="drawer-details">
                <Field label="Stage">
                  <select data-testid="edit-stage" value={lead.stage} onChange={(e) => changeStage(e.target.value)} className={inputCls}>
                    {STAGE_ORDER.map((k) => <option key={k} value={k} className="bg-ink-800">{STAGE_META[k].label}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <div className="glass rounded-2xl p-4"><div className="text-white/45 text-xs mb-1">Est. Monthly Value</div><div className="font-serif text-xl text-white">{money(form?.estimated_monthly_value)}</div></div>
                  <div className="glass rounded-2xl p-4"><div className="text-white/45 text-xs mb-1">Annual Contract Value</div><div className="font-serif text-xl text-gradient">{money(Number(form?.estimated_monthly_value) * 12)}</div></div>
                </div>
                <Field label="Estimated Monthly Value (CA$)">
                  <input data-testid="edit-value" type="number" value={form?.estimated_monthly_value ?? ""} onChange={(e) => setForm({ ...form, estimated_monthly_value: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Assigned Salesperson">
                  <select data-testid="edit-assigned" value={form?.assigned_to ?? ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className={inputCls}>
                    {(team || []).map((t) => <option key={t.id} value={t.name} className="bg-ink-800">{t.name} · {t.role}</option>)}
                    {form?.assigned_to && !(team || []).some((t) => t.name === form.assigned_to) && <option value={form.assigned_to} className="bg-ink-800">{form.assigned_to}</option>}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Priority">
                    <select data-testid="edit-priority" value={form?.priority ?? "medium"} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                      {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k} className="bg-ink-800">{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Follow-up Date">
                    <input data-testid="edit-followup" type="date" value={form?.follow_up_date || ""} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                <Field label="Next Action">
                  <input data-testid="edit-next-action" value={form?.next_action ?? ""} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className={inputCls} placeholder="e.g. Send proposal" />
                </Field>
                <button data-testid="lead-save" onClick={save} className="btn-pill bg-white text-ink-950 px-6 py-3 text-sm font-600 flex items-center gap-2"><Save className="h-4 w-4" /> Save Changes</button>

                <div className="pt-2 text-sm text-white/60 space-y-1 border-t border-white/8">
                  <div><span className="text-white/40">Industry:</span> {lead.industry || "—"} · {lead.building_size || "—"}</div>
                  <div><span className="text-white/40">Frequency:</span> {lead.frequency || "—"}</div>
                </div>
              </div>
            ) : (
              <div data-testid="drawer-timeline">
                <div className="flex items-center justify-between mb-4 gap-2">
                  <select data-testid="activity-filter" value={filter} onChange={(e) => { setFilter(e.target.value); loadActivity(e.target.value); }} className={inputCls + " max-w-[60%]"}>
                    <option value="" className="bg-ink-800">All events</option>
                    {ACTIVITY_TYPES.map((t) => <option key={t} value={t} className="bg-ink-800">{t.replace(/_/g, " ")}</option>)}
                  </select>
                  <button data-testid="activity-export" onClick={exportCsv} className="btn-pill glass text-white/70 px-4 py-2 text-xs flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Export CSV</button>
                </div>
                <div className="relative pl-6">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" />
                  {activity.length === 0 ? <p className="text-white/40 text-sm">No activity yet.</p> : activity.map((a) => (
                    <div key={a.id} className="relative mb-5" data-testid={`activity-${a.type}`}>
                      <span className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full bg-steel-light ring-4 ring-ink-900" />
                      <div className="text-white text-sm">{a.message}</div>
                      <div className="text-white/40 text-xs mt-0.5 flex items-center gap-1.5"><User className="h-3 w-3" /> {a.actor} · {fmtDateTime(a.at)} · <span className="text-steel-light">{a.type.replace(/_/g, " ")}</span></div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-white/8">
                  <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note…" className={inputCls} />
                  <button data-testid="note-add" onClick={addNote} className="btn-pill bg-white text-ink-950 px-4 flex items-center"><Send className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const inputCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-2.5 text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-steel-light/60";
const Field = ({ label, children }) => (<div><label className="text-white/45 text-xs mb-1.5 block">{label}</label>{children}</div>);
